import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientsApi, projectsApi } from '../api';
import { useCanEdit, useIsAdmin } from '../auth';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Building2,
  Mail,
  Phone,
  MapPin,
  User,
  FolderKanban,
  Plus,
  ChevronRight,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import clsx from 'clsx';
import { CatalogRestrictionsEditor } from '../components/CatalogRestrictionsEditor';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCanEdit();
  const isAdmin = useIsAdmin();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Carica cliente
  const { data: clientData, isLoading, error } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.get(id!),
    enabled: Boolean(id),
  });

  // Carica progetti del cliente
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { clientId: id }],
    queryFn: () => projectsApi.list({ clientId: id, limit: 10 }),
    enabled: Boolean(id),
  });

  // Mutation per eliminazione (soft delete)
  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients');
    },
  });

  const client = clientData?.data.data.client;
  const projects = projectsData?.data.data.projects || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Cliente non trovato</h2>
        <p className="text-gray-600 mb-4">Il cliente richiesto non esiste o è stato eliminato.</p>
        <Link to="/clients" className="btn-primary inline-flex">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alla lista
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Torna ai clienti
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                {!client.active && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                    Disattivato
                  </span>
                )}
              </div>
              <p className="text-gray-500">{client.code}</p>
            </div>
          </div>

          {canEdit && (
            <div className="flex items-center space-x-2">
              <Link
                to={`/clients/${id}/edit`}
                className="btn-secondary"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifica
              </Link>
              {isAdmin && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn-danger"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Descrizione */}
          {client.description && (
            <div className="card p-6">
              <h2 className="font-semibold text-gray-900 mb-3">Descrizione</h2>
              <p className="text-gray-600">{client.description}</p>
            </div>
          )}

          {/* Progetti */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">
                Progetti ({projects.length})
              </h2>
              {canEdit && (
                <Link
                  to={`/projects/new?clientId=${id}`}
                  className="btn-primary text-xs py-1.5 px-3"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nuovo progetto
                </Link>
              )}
            </div>

            {projects.length === 0 ? (
              <div className="p-6 text-center">
                <FolderKanban className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 mb-4">Nessun progetto per questo cliente</p>
                {canEdit && (
                  <Link
                    to={`/projects/new?clientId=${id}`}
                    className="btn-primary inline-flex text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Crea il primo progetto
                  </Link>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {projects.map((project) => (
                  <li key={project.id}>
                    <Link
                      to={`/projects/${project.id}`}
                      className="flex items-center px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 truncate">
                            {project.name}
                          </p>
                          <span className={clsx(
                            'text-xs px-2 py-0.5 rounded-full',
                            project.status === 'exported' ? 'bg-green-100 text-green-700' :
                            project.status === 'valid' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          )}>
                            {project.status === 'exported' ? 'Esportato' :
                             project.status === 'valid' ? 'Validato' : 'Bozza'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          Aggiornato {new Date(project.updatedAt).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Restrizioni Catalogo */}
          {canEdit && (
            <div className="card p-6">
              <CatalogRestrictionsEditor clientId={id!} />
            </div>
          )}

          {/* Note */}
          {client.notes && (
            <div className="card p-6">
              <h2 className="font-semibold text-gray-900 mb-3">Note interne</h2>
              <p className="text-gray-600 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contatti */}
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Contatti</h2>
            
            {client.contact?.referent || client.contact?.email || client.contact?.phone || client.contact?.address ? (
              <dl className="space-y-3">
                {client.contact?.referent && (
                  <div className="flex items-start space-x-3">
                    <User className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <dt className="text-xs text-gray-500">Referente</dt>
                      <dd className="text-sm text-gray-900">{client.contact.referent}</dd>
                    </div>
                  </div>
                )}
                {client.contact?.email && (
                  <div className="flex items-start space-x-3">
                    <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <dt className="text-xs text-gray-500">Email</dt>
                      <dd className="text-sm">
                        <a href={`mailto:${client.contact.email}`} className="text-primary-600 hover:underline">
                          {client.contact.email}
                        </a>
                      </dd>
                    </div>
                  </div>
                )}
                {client.contact?.phone && (
                  <div className="flex items-start space-x-3">
                    <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <dt className="text-xs text-gray-500">Telefono</dt>
                      <dd className="text-sm">
                        <a href={`tel:${client.contact.phone}`} className="text-primary-600 hover:underline">
                          {client.contact.phone}
                        </a>
                      </dd>
                    </div>
                  </div>
                )}
                {client.contact?.address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <dt className="text-xs text-gray-500">Indirizzo</dt>
                      <dd className="text-sm text-gray-900">{client.contact.address}</dd>
                    </div>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-gray-500 text-sm">Nessun contatto inserito</p>
            )}
          </div>

          {/* Metadata */}
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Informazioni</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Creato il</dt>
                <dd className="text-gray-900">
                  {new Date(client.createdAt).toLocaleDateString('it-IT')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Modificato il</dt>
                <dd className="text-gray-900">
                  {new Date(client.updatedAt).toLocaleDateString('it-IT')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Stato</dt>
                <dd className={client.active ? 'text-green-600' : 'text-red-600'}>
                  {client.active ? 'Attivo' : 'Disattivato'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Conferma eliminazione
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler disattivare il cliente <strong>{client.name}</strong>?
              {projects.length > 0 && (
                <span className="block mt-2 text-amber-600">
                  Attenzione: questo cliente ha {projects.length} progetto/i associato/i.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="btn-danger"
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminazione...
                  </>
                ) : (
                  'Conferma eliminazione'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
